(() => {
  const STORAGE_KEY = 'american-stride-wishlist';
  const desktopCards = window.matchMedia('(min-width: 990px)');
  const observedCardMedia = new WeakSet();
  const SAVED_ITEMS_PATH = '/pages/saved-items';
  const HEART_SVG = `
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z"/>
    </svg>`;

  const normalizeUrl = (value) => {
    try {
      const url = new URL(value, location.origin);
      return `${url.origin}${url.pathname}`;
    } catch {
      return value;
    }
  };

  const readWishlist = () => {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(parsed) ? parsed.filter((item) => item?.url && item?.title) : [];
    } catch {
      return [];
    }
  };

  const writeWishlist = (items) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      return;
    }
    updateInterface();
  };

  const cleanPriceText = (value) => value?.replace(/\s+USD\b/g, '').replace(/\s+/g, ' ').trim() || '';

  const getPriceDetails = (root) => {
    const price = root.querySelector('.price');
    if (!price) return null;

    if (price.classList.contains('price--on-sale')) {
      const compareAt = cleanPriceText(price.querySelector('.price__sale s.price-item')?.textContent);
      const current = cleanPriceText(price.querySelector('.price__sale .price-item--sale')?.textContent);
      if (current) return { compareAt, current };
    }

    const current = cleanPriceText(price.querySelector('.price__regular .price-item--regular:not(s)')?.textContent);
    return current ? { compareAt: '', current } : null;
  };

  const parseStoredPrice = (value) => {
    const text = cleanPriceText(value);
    const amounts = text.match(/(?:From\s+)?[$€£]\s?[\d,]+(?:\.\d{2})?/gi) || [];
    if (!amounts.length) return null;
    const current = cleanPriceText(amounts[amounts.length - 1]);
    const compareAt = /sale price/i.test(text) && amounts.length > 1 ? cleanPriceText(amounts[0]) : '';
    return { compareAt, current };
  };

  const getTitleAdditionalColorCount = (title = '') => {
    const totalInTitle = title.match(/(\d+)\s+Colors Available/i);
    return totalInTitle ? Math.max(0, Number.parseInt(totalInTitle[1], 10) - 1) : 0;
  };

  const getAdditionalColorCount = (root, title = '') => {
    const displayed = root.querySelector('.collection-card-color-count')?.textContent.match(/\+(\d+)\s+Colors/i);
    if (displayed) return Number.parseInt(displayed[1], 10);
    return getTitleAdditionalColorCount(title);
  };

  const readProductPageAdditionalColorCount = (html, productUrl) => {
    const page = new DOMParser().parseFromString(html, 'text/html');
    const description = page.querySelector('.product__description');
    const explicitTotal = description?.textContent.match(/(\d+)\s+Colou?rs Available/i);
    let count = explicitTotal ? Math.max(0, Number.parseInt(explicitTotal[1], 10) - 1) : 0;
    page.querySelectorAll('fieldset').forEach((fieldset) => {
      const optionName = fieldset.querySelector('legend')?.textContent.split(':')[0].trim().toLowerCase();
      if (optionName !== 'color' && optionName !== 'colour') return;
      const optionValues = new Set(
        Array.from(fieldset.querySelectorAll('input[value]'), (input) => input.value.trim()).filter(Boolean),
      );
      count = Math.max(count, Math.max(0, optionValues.size - 1));
    });
    const currentPath = new URL(productUrl, location.origin).pathname.replace(/\/$/, '');
    const linkedProductPaths = new Set();

    description?.querySelectorAll('a[href*="/products/"]').forEach((link) => {
      if (!link.querySelector('img')) return;
      try {
        const path = new URL(link.href, location.origin).pathname.replace(/\/$/, '');
        if (path !== currentPath) linkedProductPaths.add(path);
      } catch {
        return;
      }
    });

    count = Math.max(count, linkedProductPaths.size);
    return count;
  };

  let colorHydrationRunning = false;
  const hydrateMissingAdditionalColorCounts = async () => {
    if (colorHydrationRunning || !document.querySelector('[data-custom-saved-items-page]')) return;
    const items = readWishlist();
    const missing = items.filter(
      (item) => !Number.isFinite(item.additionalColorCount) && getTitleAdditionalColorCount(item.title) === 0,
    );
    if (!missing.length) return;

    colorHydrationRunning = true;
    let changed = false;
    for (let index = 0; index < missing.length; index += 3) {
      const batch = missing.slice(index, index + 3);
      const results = await Promise.allSettled(
        batch.map(async (item) => {
          const url = new URL(item.url, location.origin);
          const response = await fetch(`${url.pathname}?as_saved_items_color_lookup=1`, { credentials: 'same-origin' });
          if (!response.ok) throw new Error(`Saved item color lookup failed: ${response.status}`);
          return { item, count: readProductPageAdditionalColorCount(await response.text(), item.url) };
        }),
      );

      results.forEach((result) => {
        if (result.status !== 'fulfilled') return;
        const stored = items.find((item) => item.url === result.value.item.url);
        if (!stored) return;
        stored.additionalColorCount = result.value.count;
        changed = true;
      });
    }

    colorHydrationRunning = false;
    if (!changed) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      return;
    }
    updateInterface();
  };

  const getProductFromCard = (card) => {
    const link = card.querySelector('.card__heading a[href*="/products/"], a[href*="/products/"]');
    const image = card.querySelector('.card__media img');
    const price = card.querySelector('.price')?.innerText.replace(/\s+/g, ' ').trim() || '';
    const title = card.querySelector('.card__heading')?.innerText.replace(/\s+/g, ' ').trim() || link?.textContent.trim() || '';
    return {
      url: normalizeUrl(link?.href || ''),
      title,
      image: image?.currentSrc || image?.src || '',
      alt: image?.alt || '',
      price,
      priceDetails: getPriceDetails(card),
      additionalColorCount: getAdditionalColorCount(card, title),
    };
  };

  const getProductFromPage = () => {
    const image = document.querySelector('media-gallery .product__media img, media-gallery img');
    const title = document.querySelector('.product__title h1, h1')?.innerText.replace(/\s+/g, ' ').trim() || document.title;
    return {
      url: normalizeUrl(document.querySelector('link[rel="canonical"]')?.href || location.href),
      title,
      image: image?.currentSrc || image?.src || '',
      alt: image?.alt || '',
      price: document.querySelector('.product__info-container .price')?.innerText.replace(/\s+/g, ' ').trim() || '',
      priceDetails: getPriceDetails(document.querySelector('.product__info-container') || document),
      additionalColorCount: getTitleAdditionalColorCount(title),
    };
  };

  const isSaved = (url) => readWishlist().some((item) => item.url === normalizeUrl(url));

  const toggleProduct = (product) => {
    if (!product.url || !product.title) return;
    const items = readWishlist();
    const index = items.findIndex((item) => item.url === product.url);
    if (index >= 0) items.splice(index, 1);
    else items.unshift(product);
    writeWishlist(items);
  };

  const createHeaderButton = () => {
    const button = document.createElement('a');
    button.href = SAVED_ITEMS_PATH;
    button.className = 'header__icon link focus-inset custom-wishlist-header-button';
    button.setAttribute('aria-label', 'Saved items');
    button.innerHTML = `${HEART_SVG}<span class="custom-wishlist-count" aria-hidden="true"></span>`;
    return button;
  };

  const initializeHeader = () => {
    let changed = false;
    document.querySelectorAll('header .header__icon--cart').forEach((cart) => {
      if (cart.parentElement.querySelector(':scope > .custom-wishlist-header-button')) return;
      cart.insertAdjacentElement('beforebegin', createHeaderButton());
      changed = true;
    });
    return changed;
  };

  const createCardButton = (card) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'custom-wishlist-card-button';
    button.innerHTML = HEART_SVG;
    return button;
  };

  window.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('.custom-wishlist-card-button');
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      const card = button.closest('.product-card-wrapper');
      toggleProduct(card ? getProductFromCard(card) : getProductFromPage());
    },
    true,
  );

  const initializeCards = (root = document) => {
    let changed = false;
    root.querySelectorAll('.product-card-wrapper').forEach((card) => {
      if (!card.querySelector('a[href*="/products/"]')) return;
      const media = card.querySelector('.card__inner, .media');
      if (!media) return;
      const button = card.querySelector('.custom-wishlist-card-button') || createCardButton(card);
      if (button.parentElement !== card) {
        card.append(button);
        changed = true;
      }
      const positionHeart = () => {
        if (!desktopCards.matches) return;
        const top = media.getBoundingClientRect().bottom - card.getBoundingClientRect().top - button.offsetHeight - 9;
        card.style.setProperty('--custom-card-heart-top', `${top}px`);
      };
      positionHeart();
      if (!observedCardMedia.has(media)) {
        observedCardMedia.add(media);
        new ResizeObserver(positionHeart).observe(media);
      }
    });
    return changed;
  };

  const initializeProductGallery = (root = document) => {
    if (!location.pathname.startsWith('/products/')) return false;
    const gallery = root.querySelector('media-gallery') || document.querySelector('media-gallery');
    if (!gallery) return false;
    const container = gallery.closest('.product__media-wrapper') || gallery;
    if (container.querySelector(':scope > .custom-wishlist-product-button')) return false;
    const button = createCardButton(document.body);
    button.classList.add('custom-wishlist-product-button');
    container.append(button);
    return true;
  };

  const ensureDrawer = () => {
    let drawer = document.querySelector('#CustomWishlistDrawer');
    if (drawer) return drawer;

    drawer = document.createElement('dialog');
    drawer.id = 'CustomWishlistDrawer';
    drawer.className = 'custom-wishlist-drawer';
    drawer.setAttribute('aria-labelledby', 'CustomWishlistTitle');
    drawer.innerHTML = `
      <div class="custom-wishlist-drawer__panel">
        <div class="custom-wishlist-drawer__header">
          <h2 id="CustomWishlistTitle">Saved Items</h2>
          <button type="button" class="custom-wishlist-drawer__close" aria-label="Close saved items">&times;</button>
        </div>
        <div class="custom-wishlist-drawer__items"></div>
      </div>`;
    drawer.querySelector('.custom-wishlist-drawer__close').addEventListener('click', () => drawer.close());
    drawer.addEventListener('click', (event) => {
      if (event.target === drawer) drawer.close();
    });
    document.body.append(drawer);
    return drawer;
  };

  const renderDrawer = () => {
    const container = ensureDrawer().querySelector('.custom-wishlist-drawer__items');
    const items = readWishlist();
    if (!items.length) {
      container.innerHTML = '<p class="custom-wishlist-empty">Your saved items will appear here.</p>';
      return;
    }

    container.replaceChildren(...items.map((item) => {
      const row = document.createElement('article');
      row.className = 'custom-wishlist-item';

      const link = document.createElement('a');
      link.className = 'custom-wishlist-item__link';
      link.href = item.url;
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = item.alt || item.title;
      image.loading = 'lazy';
      const details = document.createElement('span');
      details.className = 'custom-wishlist-item__details';
      const title = document.createElement('span');
      title.className = 'custom-wishlist-item__title';
      title.textContent = item.title;
      details.append(title);
      if (item.price) {
        const price = document.createElement('span');
        price.className = 'custom-wishlist-item__price';
        price.textContent = item.price;
        details.append(price);
      }
      link.append(image, details);

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'custom-wishlist-item__remove';
      remove.setAttribute('aria-label', `Remove ${item.title} from saved items`);
      remove.innerHTML = `${HEART_SVG}<span>Remove</span>`;
      remove.addEventListener('click', () => toggleProduct(item));
      row.append(link, remove);
      return row;
    }));
  };

  function openDrawer() {
    const drawer = ensureDrawer();
    renderDrawer();
    if (!drawer.open) drawer.showModal();
  }

  const savedItemImageUrl = (source) => {
    try {
      const url = new URL(source, location.origin);
      if (url.hostname.endsWith('cdn.shopify.com') || url.pathname.startsWith('/cdn/shop/')) {
        url.searchParams.set('width', '900');
      }
      return url.href;
    } catch {
      return source;
    }
  };

  const renderSavedItemsPage = () => {
    const container = document.querySelector('[data-custom-saved-items-page]');
    if (!container) return;

    const items = readWishlist();
    container.replaceChildren();

    if (!items.length) {
      const empty = document.createElement('div');
      empty.className = 'custom-saved-items-page__empty';
      empty.innerHTML = '<p>Your saved items will appear here.</p><a class="button" href="/collections/all">Continue shopping</a>';
      container.append(empty);
      return;
    }

    const grid = document.createElement('div');
    grid.className = 'custom-saved-items-page__grid';

    items.forEach((item) => {
      const card = document.createElement('article');
      card.className = 'custom-saved-items-page__item';

      const link = document.createElement('a');
      link.className = 'custom-saved-items-page__product-link';
      link.href = item.url;

      const image = document.createElement('img');
      image.className = 'custom-saved-items-page__image';
      image.src = savedItemImageUrl(item.image);
      image.alt = item.alt || item.title;
      image.loading = 'lazy';
      image.decoding = 'async';

      const title = document.createElement('h2');
      title.className = 'custom-saved-items-page__title';
      title.textContent = item.title;
      link.append(image, title);

      card.append(link);

      const priceDetails = item.priceDetails?.current ? item.priceDetails : parseStoredPrice(item.price);
      const additionalColorCount = Number.isFinite(item.additionalColorCount)
        ? item.additionalColorCount
        : getTitleAdditionalColorCount(item.title);
      if (priceDetails || additionalColorCount > 0) {
        const priceRow = document.createElement('div');
        priceRow.className = 'custom-saved-items-page__price-row';

        if (priceDetails) {
          const prices = document.createElement('p');
          prices.className = 'custom-saved-items-page__prices';
          if (priceDetails.compareAt) {
            const compareAt = document.createElement('s');
            compareAt.className = 'custom-saved-items-page__compare-price';
            compareAt.textContent = priceDetails.compareAt;
            prices.append(compareAt);
          }
          const current = document.createElement('span');
          current.className = 'custom-saved-items-page__current-price';
          current.textContent = priceDetails.current;
          prices.append(current);
          priceRow.append(prices);
        }

        if (additionalColorCount > 0) {
          const colors = document.createElement('span');
          colors.className = 'custom-saved-items-page__color-count';
          colors.textContent = `+${additionalColorCount} Colors`;
          priceRow.append(colors);
        }

        card.append(priceRow);
      }

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'custom-saved-items-page__remove';
      remove.setAttribute('aria-label', `Remove ${item.title} from saved items`);
      remove.innerHTML = `${HEART_SVG}<span>Remove</span>`;
      remove.addEventListener('click', () => toggleProduct(item));
      card.append(remove);
      grid.append(card);
    });

    container.append(grid);
    void hydrateMissingAdditionalColorCounts();
  };

  const updateInterface = () => {
    const items = readWishlist();
    const urls = new Set(items.map((item) => item.url));
    document.querySelectorAll('.custom-wishlist-card-button').forEach((button) => {
      const card = button.closest('.product-card-wrapper');
      const product = card ? getProductFromCard(card) : getProductFromPage();
      const saved = urls.has(product.url);
      button.classList.toggle('is-saved', saved);
      button.setAttribute('aria-pressed', String(saved));
      button.setAttribute('aria-label', `${saved ? 'Remove from' : 'Add to'} saved items: ${product.title}`);
    });
    document.querySelectorAll('.custom-wishlist-header-button').forEach((button) => {
      button.setAttribute('aria-label', items.length ? `Saved items, ${items.length}` : 'Saved items');
    });
    document.querySelectorAll('.custom-wishlist-count').forEach((badge) => {
      const count = items.length ? String(items.length) : '';
      if (badge.textContent !== count) badge.textContent = count;
      badge.hidden = !items.length;
    });
    if (document.querySelector('#CustomWishlistDrawer[open]')) renderDrawer();
    renderSavedItemsPage();
  };

  const initialize = (root = document) => {
    initializeHeader();
    initializeCards(root);
    initializeProductGallery(root);
    updateInterface();
  };

  initialize();
  desktopCards.addEventListener('change', () => initialize());
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  window.addEventListener('storage', updateInterface);
  new MutationObserver((mutations) => {
    if (!mutations.some((mutation) => mutation.addedNodes.length)) return;
    const headerChanged = initializeHeader();
    const cardsChanged = initializeCards();
    const galleryChanged = initializeProductGallery();
    if (headerChanged || cardsChanged || galleryChanged) updateInterface();
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
